import type { ReactElement, MouseEvent } from 'react';
import { memo, useMemo, useRef, useState } from 'react';

import type { StandardText } from '@buying/library';

import {
    AngleDownIcon,
    LockIcon,
} from '@bestseller-bit/frontend-community.theme.buying-theme';
import Checkbox from '@mui/material/Checkbox';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Popover, { type PopoverActions } from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

interface StandardTextSelectorProps {
    options: Array<StandardText>;
    selected: Array<StandardText>;
    nothingSelectedLabel: string;
    onSelect: (value: StandardText) => void;
    isDisabled?: boolean;
    isLocked?: boolean;
}

const idKey = 'standardTextNumber';
const labelKey = 'textName';
const lockedIconSize = 16;

const StandardTextSelector = memo(
    ({
        options,
        selected,
        nothingSelectedLabel,
        onSelect,
        isDisabled: isDisabledProp = false,
        isLocked = false,
    }: StandardTextSelectorProps): ReactElement => {
        const { palette } = useTheme();
        const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

        const popoverActions = useRef<PopoverActions>(null);

        const isDisabled = isDisabledProp || isLocked;

        const handleOnOpenClick = (event: MouseEvent<HTMLDivElement>): void => {
            if (!isDisabled) {
                setAnchorEl(event.currentTarget);
            }
        };

        const handleClose = (): void => {
            setAnchorEl(null);
        };

        const handleOnSelect = (value: StandardText): void => {
            onSelect(value);
        };

        const open = Boolean(anchorEl);
        const id = open ? 'simple-popover' : undefined;

        const buttonLabel = useMemo((): string => {
            if (selected.length === 0 && !isDisabled) {
                return nothingSelectedLabel;
            } else if (selected.length > 0) {
                return selected.map((item) => item[labelKey]).join(', ');
            } else {
                // If there is nothing selected and its disabled, we just don't show anything.
                return '';
            }
        }, [selected, nothingSelectedLabel, isDisabled]);

        return (
            <>
                <Stack
                    direction="row"
                    sx={{
                        cursor: isDisabled ? 'default' : 'pointer',
                        overflow: 'hidden',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    onClick={(e): void => {
                        handleOnOpenClick(e);
                    }}
                >
                    <Typography
                        variant="buttons.xsmall"
                        aria-describedby={id}
                        sx={{
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                        }}
                        onClick={handleOnOpenClick}
                    >
                        {buttonLabel}
                    </Typography>

                    {!isDisabled && (
                        <AngleDownIcon
                            size={14}
                            style={{
                                transform: open
                                    ? 'rotate(180deg)'
                                    : 'rotate(0deg)',
                                transition: 'transform 0.3s ease',
                                flexShrink: 0,
                            }}
                        />
                    )}

                    {isLocked && (
                        <Stack
                            sx={{
                                position: 'relative',
                                width: `${lockedIconSize}px`,
                                marginLeft: 1,
                            }}
                        >
                            <LockIcon
                                color={alpha(palette.primary.main, 0.4)} // 0.4 because the icon looks visually darker than the thinner texts.
                                size={lockedIconSize}
                                style={{
                                    // We do this absolute position trick because the lock icon is taller than the button
                                    position: 'absolute',
                                    top: '50%',
                                    right: 0,
                                    transform: 'translateY(-50%)',
                                }}
                            />
                        </Stack>
                    )}
                </Stack>
                <Popover
                    open={open}
                    action={popoverActions}
                    anchorEl={anchorEl}
                    anchorPosition={{ top: -2, left: 0 }}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                maxHeight: 320,
                                maxWidth: 240,
                            },
                        },
                    }}
                    onClose={handleClose}
                >
                    {options.map((value) => (
                        <ListItem
                            key={value[idKey]}
                            sx={{
                                paddingInline: 0,
                                paddingBlock: 0,
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                handleOnSelect(value);
                            }}
                        >
                            <ListItemButton
                                sx={{
                                    paddingInline: 3,
                                    paddingBlock: 2.5,
                                }}
                            >
                                <Checkbox
                                    size="small"
                                    checked={selected.includes(value)}
                                    sx={{
                                        marginRight: 3,
                                    }}
                                />

                                <Typography
                                    variant="buttons.xsmall"
                                    sx={{
                                        width: 1,
                                    }}
                                >
                                    {value[labelKey]}
                                </Typography>
                            </ListItemButton>
                        </ListItem>
                    ))}
                </Popover>
            </>
        );
    },
    (prevProps, nextProps) => prevProps.selected === nextProps.selected
);

StandardTextSelector.displayName = 'StandardTextSelector';

export { StandardTextSelector };
export type { StandardTextSelectorProps };
